// src/components/PortalVerificacao/ExportarPDF.tsx
// Botão de exportação PDF — usa window.print() com estilos específicos
// CREATED: 2026-05-07 — v1.0

import React, { useState } from 'react';
import { Download, Loader2, FileDown, CheckCircle } from 'lucide-react';
import { VerificacaoData, STATUS_CONFIG, formatDate, formatCurrency } from '../../services/verificacaoService';

const BRAND = { gold: '#D4AF37', goldDeep: '#B88917', charcoal: '#1F2328' };

interface Props {
  data: VerificacaoData;
  protocolo: string;
  variant?: 'full' | 'compact';
}

const ExportarPDF: React.FC<Props> = ({ data, protocolo, variant = 'full' }) => {
  const [exportando, setExportando] = useState(false);
  const [concluido,  setConcluido]  = useState(false);

  const handleExportar = async () => {
    setExportando(true);

    // Injeta estilos de impressão temporários
    const styleId = 'portal-print-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        @media print {
          body * { visibility: hidden !important; }
          #portal-verificacao-conteudo,
          #portal-verificacao-conteudo * { visibility: visible !important; }
          #portal-verificacao-conteudo { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
          video { display: none !important; }
          .print-page-break { page-break-before: always; }
          @page { margin: 1.5cm; size: A4; }
          body { background: white !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `;
      document.head.appendChild(style);
    }

    await new Promise<void>(res => setTimeout(res, 300));
    window.print();

    setExportando(false);
    setConcluido(true);
    setTimeout(() => setConcluido(false), 3000);
  };

  if (variant === 'compact') {
    return (
      <button
        onClick={handleExportar}
        disabled={exportando}
        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border transition hover:opacity-80 disabled:opacity-50"
        style={{ borderColor: `${BRAND.gold}55`, color: BRAND.charcoal }}
      >
        {exportando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        {exportando ? 'Gerando...' : 'PDF'}
      </button>
    );
  }

  return (
    <div className="rounded-3xl border bg-white p-5 shadow-sm" style={{ borderColor: `${BRAND.gold}33` }}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-2xl" style={{ backgroundColor: `${BRAND.gold}1A` }}>
            <FileDown className="w-5 h-5" style={{ color: BRAND.goldDeep }} />
          </div>
          <div>
            <p className="text-sm font-black" style={{ color: BRAND.charcoal }}>Exportar Comprovante</p>
            <p className="text-xs text-gray-500">Salve seus dados e documentos em PDF</p>
          </div>
        </div>
        <button
          onClick={handleExportar}
          disabled={exportando}
          className="flex items-center gap-2 px-5 py-2.5 rounded-2xl font-black text-sm text-white transition hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: BRAND.charcoal }}
        >
          {concluido ? (
            <><CheckCircle className="w-4 h-4 text-green-400" /> Pronto!</>
          ) : exportando ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Gerando...</>
          ) : (
            <><Download className="w-4 h-4" /> Exportar PDF</>
          )}
        </button>
      </div>

      {/* Conteúdo oculto que será impresso */}
      <div id="portal-verificacao-conteudo" className="hidden print:block">
        <div style={{ fontFamily: 'Arial, sans-serif', padding: '20px', color: '#1F2328' }}>
          {/* Cabeçalho do PDF */}
          <div style={{ borderBottom: '2px solid #D4AF37', paddingBottom: '16px', marginBottom: '24px' }}>
            <h1 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
              Federação dos Profissionais da Beleza
            </h1>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0' }}>
              Comprovante de Cadastro de Filiação
            </p>
          </div>

          {/* Protocolo */}
          <div style={{ backgroundColor: '#fef3c7', border: '1px solid #D4AF37', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
            <p style={{ fontSize: '11px', fontWeight: 'bold', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Número do Protocolo</p>
            <p style={{ fontSize: '13px', fontFamily: 'monospace', fontWeight: 'bold', color: '#1F2328', margin: '4px 0 0' }}>{protocolo}</p>
          </div>

          {/* Status */}
          <table style={{ width: '100%', marginBottom: '20px', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', fontSize: '12px', color: '#6b7280', width: '140px' }}>Status</td>
                <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', fontSize: '12px', fontWeight: 'bold', color: STATUS_CONFIG[data.status].color }}>
                  {STATUS_CONFIG[data.status].label}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', fontSize: '12px', color: '#6b7280' }}>Nome / Escola</td>
                <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', fontSize: '12px', fontWeight: 'bold' }}>{data.schoolName}</td>
              </tr>
              {data.responsibleName && (
                <tr>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', fontSize: '12px', color: '#6b7280' }}>Responsável</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', fontSize: '12px' }}>{data.responsibleName}</td>
                </tr>
              )}
              {data.email && (
                <tr>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', fontSize: '12px', color: '#6b7280' }}>E-mail</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', fontSize: '12px' }}>{data.email}</td>
                </tr>
              )}
              {data.city && (
                <tr>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', fontSize: '12px', color: '#6b7280' }}>Localização</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', fontSize: '12px' }}>{data.city}{data.state ? `, ${data.state}` : ''}</td>
                </tr>
              )}
              {data.paymentValue && (
                <tr>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', fontSize: '12px', color: '#6b7280' }}>Valor da Filiação</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', fontSize: '12px', fontWeight: 'bold' }}>{formatCurrency(data.paymentValue)}</td>
                </tr>
              )}
              <tr>
                <td style={{ padding: '8px', fontSize: '12px', color: '#6b7280' }}>Data do Cadastro</td>
                <td style={{ padding: '8px', fontSize: '12px' }}>{formatDate(data.createdAt)}</td>
              </tr>
            </tbody>
          </table>

        {/* Documentos */}
{data.documentos && data.documentos.length > 0 && (
  <>
    <h2 style={{ fontSize: '14px', fontWeight: 'bold', margin: '20px 0 12px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
      Documentos Enviados ({data.documentos.length})
    </h2>

    {/* Imagens */}
    {data.documentos.filter(doc => doc.mimeType?.startsWith('image/')).length > 0 && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        {data.documentos
          .filter(doc => doc.mimeType?.startsWith('image/'))
          .map(doc => (
            <div key={doc.id} style={{ textAlign: 'center', maxWidth: '200px' }}>
              <img
                src={doc.url}
                alt={doc.description || doc.fileName}
                style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e5e7eb' }}
              />
              <p style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>
                {doc.description || doc.fileName}
              </p>
            </div>
          ))}
      </div>
    )}

    {/* Tabela de todos os docs */}
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
      <thead>
        <tr style={{ backgroundColor: '#f9fafb' }}>
          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>Arquivo</th>
          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>Tipo</th>
          <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>Tamanho</th>
        </tr>
      </thead>
      <tbody>
        {data.documentos.map(doc => (
          <tr key={doc.id}>
            <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>{doc.description || doc.fileName}</td>
            <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', color: '#6b7280' }}>{doc.documentType || '—'}</td>
            <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', color: '#6b7280' }}>{(doc.fileSize / 1024).toFixed(0)} KB</td>
          </tr>
        ))}
      </tbody>
    </table>
  </>
)}

          {/* Rodapé */}
          <div style={{ marginTop: '32px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
            <p>Documento gerado em {new Date().toLocaleString('pt-BR')} · Federação dos Profissionais da Beleza</p>
            <p style={{ marginTop: '4px' }}>Para verificar a autenticidade deste documento, acesse o portal de verificação com o protocolo acima.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportarPDF;